import { useCallback, useEffect, useRef, useState } from 'react';

import { setCallActive } from './call-activity';
import { startCallRecorder, type CallRecorder } from './call-recorder';
import { useCallWakeLock } from './use-call-wake-lock';
import {
  acceptCall,
  createCall,
  declineCall,
  fetchCallPermission,
  fetchCallLines,
  fetchCallStatus,
  pingCall,
  releaseCallLine,
  uploadCallRecording,
  type WhatsAppCallDirection,
  type WhatsAppCallLines,
  type WhatsAppCallStatus,
} from './whatsapp-calls.api';

import { extractServerError } from '@/lib/api-error';

/**
 * Browser softphone for WhatsApp calls, both directions
 * (docs/integrations/doubletick-whatsapp-calling.md §8). Lives at the widget
 * root so a call survives closing the chat or changing page, and the call
 * window can be minimised.
 *
 * Outbound: mic -> RTCPeerConnection -> createOffer -> POST create ->
 * setRemoteDescription(answer).
 * Inbound: CALL_INCOMING_INITIATED arrives over SSE -> ring -> on accept, the
 * customer's SDP offer (stored from the webhook) -> createAnswer -> POST accept.
 *
 * Call state comes only from webhooks (pushed over SSE, and polled as a
 * backup). The peer connection is NOT a pickup signal: WhatsApp connects
 * media while the phone is still ringing, so only CALL_ACCEPTED starts the
 * timer on an outgoing call. Where webhooks do not reach this environment
 * (local dev) an outgoing call stays on "Ringing" - wrong but harmless.
 *
 * Every call that gets media is recorded in the browser (both sides mixed)
 * and uploaded when it ends - see call-recorder.ts.
 *
 * Concurrency is capped by the account's DoubleTick calling licenses, one
 * "line" each: the API hands one out per call and 409s ALL_LINES_BUSY when
 * none is free. Occupancy arrives over SSE, so the dial button comes back by
 * itself the moment a line frees.
 */

export type SoftphoneStatus =
  | 'idle'
  | 'checking' // consent lookup
  | 'dialing' // offer sent, waiting for the provider
  | 'ringing' // outgoing: customer's phone is ringing
  | 'incoming' // inbound call ringing here
  | 'answering' // inbound: accept in flight
  | 'connected' // talking
  | 'ended' // call over - summary shown until dismissed
  | 'failed';

/** Why the call ended, for the summary line. */
export type SoftphoneEndReason = 'hungup' | 'completed' | 'rejected' | 'missed' | 'dropped';

export interface SoftphonePeer {
  /** Digits with country code, no '+'. */
  phone: string;
  name: string | null;
}

export interface SoftphoneState {
  status: SoftphoneStatus;
  callId: string | null;
  direction: WhatsAppCallDirection | null;
  peer: SoftphonePeer | null;
  /** Seconds since the call was answered. */
  elapsed: number;
  muted: boolean;
  /** Call window collapsed to the pill. */
  minimized: boolean;
  /** Operator-facing reason when status is `failed`. */
  error: string | null;
  endReason: SoftphoneEndReason | null;
  /** Calling-line occupancy; null until the first read. */
  lines: WhatsAppCallLines | null;
}

/** An SSE `whatsapp-call` frame (NotificationsStreamService.publishWhatsAppCallEvent). */
export interface WhatsAppCallStreamEvent {
  callId: string;
  direction: WhatsAppCallDirection;
  status: WhatsAppCallStatus;
  phone: string;
  contactName: string | null;
  conversationId: string | null;
  leadId: string | null;
  /** Provider's pickup time (ISO) - what the call timer counts from. */
  startedAt: string | null;
  canAnswer: boolean;
}

const IDLE: SoftphoneState = {
  status: 'idle',
  callId: null,
  direction: null,
  peer: null,
  elapsed: 0,
  muted: false,
  minimized: false,
  error: null,
  endReason: null,
  lines: null,
};

/** Keepalive cadence - provider caps pings at 15/min; 10s leaves headroom. */
const PING_INTERVAL_MS = 10_000;
/** Webhook-backed status poll while a call is up - backup for the SSE push. */
const STATUS_POLL_MS = 2_000;
/** WhatsApp gives up ringing at ~60s; stop waiting a little after that. */
const RING_TIMEOUT_MS = 75_000;
/** The end-of-call summary clears itself after this long. */
const SUMMARY_MS = 5_000;

const TERMINAL: ReadonlySet<WhatsAppCallStatus> = new Set(['END', 'REJECT', 'MISSED', 'FAILED']);

const endReasonFor = (status: WhatsAppCallStatus, answered: boolean): SoftphoneEndReason => {
  if (status === 'REJECT') return 'rejected';
  if (status === 'MISSED') return 'missed';
  if (status === 'FAILED') return 'dropped';
  return answered ? 'completed' : 'missed';
};

/** Stable per-browser device id so the provider can tell our tabs apart. */
const deviceId = (): string => {
  try {
    const key = 'utazzo.whatsapp.deviceId';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = `admin-${crypto.randomUUID()}`;
    localStorage.setItem(key, fresh);
    return fresh;
  } catch {
    return 'admin-browser';
  }
};

const errorText = (err: unknown, fallback: string): string =>
  err instanceof Error && !('statusCode' in err) && !('isAxiosError' in err)
    ? err.message
    : (extractServerError(err).message ?? fallback);

/**
 * DoubleTick's "Invalid call state. Call cannot be answered." means the ring
 * was already over (customer gave up, or it was handled elsewhere) by the
 * time our answer landed. Say that, not the provider's raw 422.
 */
/** Events a browser counts as a user gesture for starting audio (not touchstart). */
const GESTURES = ['touchend', 'click', 'keydown'] as const;

/**
 * The caller's voice was blocked from playing (nothing on this page had been
 * tapped yet - e.g. it was opened from an alert). Play it on the next tap
 * anywhere, once, instead of leaving a connected call silent.
 */
export const playOnNextTap = (audio: HTMLAudioElement): void => {
  const retry = (): void => {
    for (const type of GESTURES) window.removeEventListener(type, retry, { capture: true });
    void audio.play().catch(() => undefined);
  };
  for (const type of GESTURES) {
    window.addEventListener(type, retry, { capture: true, passive: true });
  }
};

const CALL_GONE = /invalid call state|cannot be answered/i;
export const CALL_GONE_MESSAGE = 'This call ended before it could be answered.';

export const answerErrorText = (err: unknown): string => {
  const text = errorText(err, 'Could not answer the call.');
  return CALL_GONE.test(text) ? CALL_GONE_MESSAGE : text;
};

export const useWhatsAppCall = () => {
  const [state, setState] = useState<SoftphoneState>(IDLE);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Ringing, dialling or talking - "Update now" waits until it's over.
  const busy = !['idle', 'ended', 'failed'].includes(state.status);
  useEffect(() => {
    setCallActive(busy);
  }, [busy]);
  useEffect(() => () => setCallActive(false), []);
  // Phones: the screen stays on while it rings / talks (no lock = no drop).
  useCallWakeLock(busy);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const remoteRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recorderRef = useRef<CallRecorder | null>(null);
  const pingRef = useRef<number | null>(null);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const ringTimeoutRef = useRef<number | null>(null);
  const summaryRef = useRef<number | null>(null);
  const callIdRef = useRef<string | null>(null);
  /** Who and which way - read by the recording upload. */
  const callMetaRef = useRef<{ phone: string; direction: WhatsAppCallDirection } | null>(null);
  /**
   * Set synchronously when a call starts. `pcRef` is only filled after the
   * permission check and mic prompt, so without this a second click in that
   * window starts a second dial and DoubleTick 409s it.
   */
  const busyRef = useRef(false);
  const answeredRef = useRef(false);
  /** Mirrors state.muted so toggleMute never reads a stale render. */
  const mutedRef = useRef(false);
  /** True once a webhook for this call has been seen (poll or push). */
  const webhookSeenRef = useRef(false);

  const clearTimers = useCallback(() => {
    for (const ref of [pingRef, pollRef, tickRef]) {
      if (ref.current) window.clearInterval(ref.current);
      ref.current = null;
    }
    if (ringTimeoutRef.current) window.clearTimeout(ringTimeoutRef.current);
    ringTimeoutRef.current = null;
  }, []);

  /** Stop the recorder and upload in the background - never blocks the UI. */
  const flushRecording = useCallback(() => {
    const recorder = recorderRef.current;
    recorderRef.current = null;
    const callId = callIdRef.current;
    const meta = callMetaRef.current;
    if (!recorder) return;
    void recorder
      .stop()
      .then((blob) => {
        if (!blob || !callId || !meta) return;
        return uploadCallRecording({ callId, ...meta, blob });
      })
      .catch((err: unknown) => {
        console.warn('WhatsApp call recording upload failed', err);
      });
  }, []);

  const teardown = useCallback(() => {
    clearTimers();
    // Hand the calling line back even when the call ended without a hang-up
    // request (customer dropped, media failed). Idempotent server-side.
    const endedCallId = callIdRef.current;
    if (endedCallId && pcRef.current) releaseCallLine(endedCallId).catch(() => undefined);
    // Recorder first: stop() flushes what it has before the tracks go away.
    flushRecording();
    micRef.current?.getTracks().forEach((t) => t.stop());
    micRef.current = null;
    remoteRef.current = null;
    if (pcRef.current) {
      // Detach handlers first so close() does not re-enter the state machine.
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.ontrack = null;
      pcRef.current.close();
    }
    pcRef.current = null;
    if (audioRef.current) {
      audioRef.current.srcObject = null;
      audioRef.current.remove();
      audioRef.current = null;
    }
    callIdRef.current = null;
    callMetaRef.current = null;
    busyRef.current = false;
    answeredRef.current = false;
    mutedRef.current = false;
    webhookSeenRef.current = false;
  }, [clearTimers, flushRecording]);

  const fail = useCallback(
    (message: string) => {
      const { peer, direction, lines } = stateRef.current;
      teardown();
      setState({ ...IDLE, status: 'failed', peer, direction, lines, error: message });
    },
    [teardown],
  );

  /** End of a call that got as far as a callId: show the summary briefly. */
  const finish = useCallback(
    (reason: SoftphoneEndReason) => {
      teardown();
      setState((s) => ({ ...s, status: 'ended', muted: false, endReason: reason }));
      if (summaryRef.current) window.clearTimeout(summaryRef.current);
      summaryRef.current = window.setTimeout(() => {
        setState((s) => (s.status === 'ended' ? { ...IDLE, lines: s.lines } : s));
      }, SUMMARY_MS);
    },
    [teardown],
  );

  /** Drop a ringing inbound call without a summary (answered elsewhere, missed). */
  const stopRinging = useCallback(() => {
    teardown();
    setState((s) => ({ ...IDLE, lines: s.lines }));
  }, [teardown]);

  /**
   * `startedAt` is the provider's own pickup time. Counting from it, rather
   * than from the moment the webhook reached us, keeps our timer in step with
   * the one on the customer's phone - the push and the 2s poll both arrive
   * late, and an incrementing counter would carry that lateness forever.
   */
  const markAnswered = useCallback((startedAt?: string | null) => {
    if (answeredRef.current) return;
    answeredRef.current = true;
    if (ringTimeoutRef.current) window.clearTimeout(ringTimeoutRef.current);
    ringTimeoutRef.current = null;
    const parsed = startedAt ? Date.parse(startedAt) : Number.NaN;
    // A clock-skewed or future timestamp would show a negative timer.
    const base = Number.isFinite(parsed) && parsed <= Date.now() ? parsed : Date.now();
    const since = (): number => Math.max(0, Math.floor((Date.now() - base) / 1000));
    setState((s) => ({ ...s, status: 'connected', elapsed: since() }));
    tickRef.current = window.setInterval(() => setState((s) => ({ ...s, elapsed: since() })), 1000);
  }, []);

  /** One webhook status for the current call, from the SSE push or the poll. */
  const applyStatus = useCallback(
    (status: WhatsAppCallStatus, startedAt?: string | null) => {
      webhookSeenRef.current = true;
      const current = stateRef.current.status;
      if (current === 'incoming') {
        // Still ringing here, so any progress means someone else handled it.
        if (status !== 'INITIATED') stopRinging();
        return;
      }
      if (status === 'IN_PROGRESS') {
        // Our own accept also produces IN_PROGRESS; answer() starts the timer.
        if (current !== 'answering') markAnswered(startedAt);
        return;
      }
      if (TERMINAL.has(status)) finish(endReasonFor(status, answeredRef.current));
    },
    [finish, markAnswered, stopRinging],
  );

  const startPolling = useCallback(
    (callId: string) => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = window.setInterval(() => {
        fetchCallStatus(callId)
          .then((row) => {
            if (row && callIdRef.current === callId) applyStatus(row.status, row.startedAt);
          })
          .catch(() => undefined); // transient - next tick retries
      }, STATUS_POLL_MS);
    },
    [applyStatus],
  );

  const startPinging = useCallback((callId: string) => {
    if (pingRef.current) window.clearInterval(pingRef.current);
    pingRef.current = window.setInterval(() => {
      pingCall(callId).catch(() => undefined);
    }, PING_INTERVAL_MS);
  }, []);

  /** Mic + peer connection + remote audio + recorder hook-up. Null on mic denial. */
  const openMedia = useCallback(async (): Promise<RTCPeerConnection | null> => {
    let mic: MediaStream;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      fail('Microphone access was denied.');
      return null;
    }
    micRef.current = mic;

    // Plain config, per DoubleTick's reference. The WhatsApp side hands its
    // own candidates back in the SDP.
    const pc = new RTCPeerConnection();
    pcRef.current = pc;
    mic.getTracks().forEach((track) => pc.addTrack(track, mic));

    const audio = document.createElement('audio');
    audio.autoplay = true;
    audioRef.current = audio;
    pc.ontrack = (ev) => {
      const remote = ev.streams[0] ?? new MediaStream([ev.track]);
      remoteRef.current = remote;
      audio.srcObject = remote;
      // Blocked when nothing on this page was tapped yet (e.g. opened from an
      // alert): play on the next tap anywhere instead of staying silent.
      void audio.play().catch(() => playOnNextTap(audio));
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected' && !recorderRef.current) {
        const remote = remoteRef.current;
        if (micRef.current && remote) {
          recorderRef.current = startCallRecorder(micRef.current, remote);
        }
      }
      // 'disconnected' is often a transient blip that recovers on its own;
      // only a hard 'failed' ends the call from the media side.
      if (pc.connectionState === 'failed') {
        if (answeredRef.current) finish('dropped');
        else fail('Call connection failed (network / NAT). Try again.');
      }
    };
    return pc;
  }, [fail, finish]);

  // ---- Outgoing -------------------------------------------------------------

  const dial = useCallback(
    async (phone: string, name: string | null = null) => {
      if (busyRef.current || pcRef.current) return; // one call at a time
      busyRef.current = true;
      if (summaryRef.current) window.clearTimeout(summaryRef.current);
      callMetaRef.current = { phone, direction: 'OUTGOING' };
      setState((s) => ({
        ...IDLE,
        lines: s.lines,
        status: 'checking',
        direction: 'OUTGOING',
        peer: { phone, name },
      }));

      // Consent gate first - WhatsApp rejects the call without it, and the
      // provider's reason is more useful than a generic create failure.
      try {
        const permission = await fetchCallPermission(phone);
        if (!permission.allowed) {
          fail(permission.reason ?? 'Customer has not allowed WhatsApp calls.');
          return;
        }
      } catch (err) {
        fail(extractServerError(err).message ?? 'Could not check call permission.');
        return;
      }

      const pc = await openMedia();
      if (!pc) return;

      try {
        // Send the offer as created, before ICE gathering adds candidate
        // lines - that bare offer is the shape DoubleTick verified working.
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const localSdp = offer.sdp;
        if (!localSdp) throw new Error('No local SDP.');

        setState((s) => ({ ...s, status: 'dialing' }));
        const { callId, answer } = await createCall({ phone, sdp: localSdp, deviceId: deviceId() });
        // Cast: the guard at the top of dial() narrowed pcRef.current to null,
        // but it has changed since.
        if ((pcRef.current as RTCPeerConnection | null) !== pc) {
          // Hung up while the request was in flight - the customer's phone is
          // ringing by now, so end that call instead of orphaning it.
          declineCall(callId).catch(() => undefined);
          return;
        }
        callIdRef.current = callId;
        setState((s) => ({ ...s, callId, status: answeredRef.current ? s.status : 'ringing' }));

        // The API normalises the answer; the trailing CRLF guard is for an
        // older API still serving a trimmed SDP.
        await pc.setRemoteDescription({
          type: 'answer',
          sdp: answer.endsWith('\n') ? answer : `${answer}\r\n`,
        });

        startPinging(callId);
        startPolling(callId);
        ringTimeoutRef.current = window.setTimeout(() => {
          if (answeredRef.current || callIdRef.current !== callId) return;
          // No webhook at all means we cannot tell ringing from talking -
          // never cut what may be a live conversation.
          if (!webhookSeenRef.current) return;
          finish('missed');
          declineCall(callId).catch(() => undefined);
        }, RING_TIMEOUT_MS);
      } catch (err) {
        const callId = callIdRef.current;
        // The provider may already be ringing the customer - hang that up.
        if (callId) declineCall(callId).catch(() => undefined);
        fail(errorText(err, 'Could not place the call.'));
      }
    },
    [fail, finish, openMedia, startPinging, startPolling],
  );

  // ---- Incoming -------------------------------------------------------------

  /** Feed from the SSE `whatsapp-call-lines` stream. */
  const onLinesEvent = useCallback((lines: WhatsAppCallLines) => {
    setState((s) => ({ ...s, lines: { ...lines, free: Math.max(0, lines.total - lines.busy) } }));
  }, []);

  // First read - after that the stream keeps it current.
  useEffect(() => {
    let alive = true;
    fetchCallLines()
      .then((lines) => {
        if (alive) setState((s) => ({ ...s, lines }));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  /** Feed from the SSE `whatsapp-call` stream. */
  const onCallEvent = useCallback(
    (e: WhatsAppCallStreamEvent) => {
      if (e.callId === callIdRef.current) {
        applyStatus(e.status, e.startedAt);
        return;
      }
      if (e.direction !== 'INCOMING' || e.status !== 'INITIATED') return;
      // Already on (or placing) a call - the other agents' browsers ring.
      if (busyRef.current || pcRef.current) return;
      const s = stateRef.current.status;
      if (s !== 'idle' && s !== 'ended' && s !== 'failed') return;

      if (summaryRef.current) window.clearTimeout(summaryRef.current);
      callIdRef.current = e.callId;
      callMetaRef.current = { phone: e.phone, direction: 'INCOMING' };
      setState((s) => ({
        ...IDLE,
        lines: s.lines,
        status: 'incoming',
        callId: e.callId,
        direction: 'INCOMING',
        peer: { phone: e.phone, name: e.contactName },
      }));
      // Backup for a dropped push: the poll sees it answered elsewhere / missed.
      startPolling(e.callId);
      ringTimeoutRef.current = window.setTimeout(() => {
        if (stateRef.current.status === 'incoming' && callIdRef.current === e.callId) {
          stopRinging();
        }
      }, RING_TIMEOUT_MS);
    },
    [applyStatus, startPolling, stopRinging],
  );

  const answer = useCallback(async () => {
    const callId = callIdRef.current;
    if (!callId || stateRef.current.status !== 'incoming') return;
    busyRef.current = true;
    setState((s) => ({ ...s, status: 'answering' }));

    let offerSdp: string | null = null;
    try {
      offerSdp = (await fetchCallStatus(callId))?.offerSdp ?? null;
    } catch {
      // Handled below as "no offer".
    }
    if (!offerSdp) {
      fail(
        'This call cannot be answered here - DoubleTick did not send the audio offer. Answer it on the WhatsApp phone.',
      );
      return;
    }

    const pc = await openMedia();
    if (!pc) return;
    try {
      await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
      const local = await pc.createAnswer();
      await pc.setLocalDescription(local);
      if (!local.sdp) throw new Error('No local SDP.');
      await acceptCall({ callId, sdp: local.sdp, deviceId: deviceId() });
      if (pcRef.current !== pc) return; // hung up while accepting
      markAnswered();
      startPinging(callId);
      startPolling(callId);
    } catch (err) {
      declineCall(callId).catch(() => undefined);
      fail(answerErrorText(err));
    }
  }, [fail, markAnswered, openMedia, startPinging, startPolling]);

  // ---- Controls -------------------------------------------------------------

  /** End a live call, cancel an outgoing one, or reject a ringing inbound one. */
  const hangUp = useCallback(async () => {
    const callId = callIdRef.current;
    if (stateRef.current.status === 'incoming') stopRinging();
    else finish('hungup');
    if (callId) {
      // Best-effort: CALL_ENDED / CALL_REJECTED is the record of truth.
      await declineCall(callId).catch(() => undefined);
    }
  }, [finish, stopRinging]);

  const toggleMute = useCallback(() => {
    // Mute what is actually being sent: the peer connection's audio senders,
    // plus the mic stream in case a sender has not attached its track yet.
    const tracks = new Set<MediaStreamTrack>(micRef.current?.getAudioTracks() ?? []);
    pcRef.current?.getSenders().forEach((sender) => {
      if (sender.track?.kind === 'audio') tracks.add(sender.track);
    });
    if (tracks.size === 0) return; // no live call to mute
    const next = !mutedRef.current;
    mutedRef.current = next;
    tracks.forEach((t) => {
      t.enabled = !next;
    });
    setState((s) => ({ ...s, muted: next }));
  }, []);

  const setMinimized = useCallback((minimized: boolean) => {
    setState((s) => (s.status === 'idle' ? s : { ...s, minimized }));
  }, []);

  /** Clears a failure or the end-of-call summary. */
  const dismiss = useCallback(() => {
    if (summaryRef.current) window.clearTimeout(summaryRef.current);
    setState((s) =>
      s.status === 'failed' || s.status === 'ended' ? { ...IDLE, lines: s.lines } : s,
    );
  }, []);

  // Leaving the app mid-call must not leave a mic open or a call running. A
  // call that is only ringing here is left alone - another agent may take it.
  useEffect(
    () => () => {
      const callId = callIdRef.current;
      if (callId && pcRef.current) declineCall(callId).catch(() => undefined);
      if (summaryRef.current) window.clearTimeout(summaryRef.current);
      teardown();
    },
    [teardown],
  );

  return {
    state,
    dial,
    answer,
    hangUp,
    toggleMute,
    setMinimized,
    dismiss,
    onCallEvent,
    onLinesEvent,
  };
};

export type Softphone = ReturnType<typeof useWhatsAppCall>;
